<script setup lang="ts">
// The inbox in the shell's foot: the unread count, and a panel with the latest
// notifications and "See all". It opens beside the rail on desktop and under the
// header on phones.
import { useNotificationsStore } from '@itc/sdk';
import type { NotificationItem } from '@itc/shared';
import { Button, EmptyState, Icon, List, ListItem } from '@itc/ui';
import { computed, onBeforeUnmount, ref } from 'vue';
import { useRouter } from 'vue-router';
import NotificationView from './NotificationView.vue';

const store = useNotificationsStore();
const router = useRouter();
const open = ref(false);
const root = ref<HTMLElement | null>(null);

const label = computed(() => (store.unreadCount === 0 ? 'Notifications' : `Notifications, ${String(store.unreadCount)} unread`));

async function openItem(item: NotificationItem): Promise<void> {
  open.value = false;
  if (item.readAt === null) await store.markRead(item.id);
  if (item.link !== null) await router.push(item.link);
}

async function seeAll(): Promise<void> {
  open.value = false;
  await router.push('/inbox');
}

const onOutside = (event: MouseEvent): void => {
  if (open.value && root.value !== null && !root.value.contains(event.target as Node)) open.value = false;
};
document.addEventListener('click', onOutside);
onBeforeUnmount(() => {
  document.removeEventListener('click', onOutside);
});
</script>

<template>
  <div ref="root" class="inbox-button">
    <button type="button" class="itc-rail__item inbox-button__bell" :aria-label="label" :aria-expanded="open" @click="open = !open">
      <Icon name="inbox" />
      <span class="inbox-button__label" aria-hidden="true">Inbox</span>
      <span v-if="store.unreadCount > 0" class="itc-rail__badge inbox-button__count" aria-hidden="true">{{ store.unreadCount }}</span>
    </button>
    <div v-if="open" class="inbox-button__panel itc-panel" role="dialog" aria-label="Latest notifications">
      <div class="itc-panel__head">
        <h2 class="itc-panel__title">Notifications</h2>
        <Button v-if="store.unreadCount > 0" size="sm" variant="ghost" @click="store.markAllRead()">Mark all read</Button>
      </div>
      <List v-if="store.recent.length > 0" label="Latest notifications">
        <ListItem v-for="item in store.recent" :key="item.id" :unread="item.readAt === null" interactive @click="openItem(item)">
          <NotificationView :item="item" />
        </ListItem>
      </List>
      <EmptyState v-else title="Nothing yet" body="Notifications from ITC Web show up here." />
      <div class="inbox-button__foot">
        <Button size="sm" variant="ghost" @click="seeAll">See all</Button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.inbox-button {
  position: relative;
}
.inbox-button__bell {
  width: 100%;
}
.inbox-button__bell[aria-expanded='true'] {
  color: var(--itc-fg);
  background: var(--itc-panel-2);
}
.inbox-button__panel {
  position: absolute;
  bottom: 0;
  left: calc(100% + 16px);
  z-index: 40;
  width: min(380px, calc(100vw - var(--itc-rail-width) - 32px));
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  background: var(--itc-panel);
  border-color: var(--itc-line-2);
  box-shadow: var(--itc-shadow-pop);
}
.inbox-button__foot {
  display: flex;
  justify-content: center;
  padding: var(--itc-s2);
  border-top: 1px solid var(--itc-line);
}
@media (max-width: 720px) {
  .inbox-button__bell {
    min-width: var(--itc-tap);
    min-height: var(--itc-tap);
  }
  .inbox-button__label {
    display: none;
  }
  .inbox-button__count {
    top: 2px;
    right: 0;
  }
  .inbox-button__panel {
    position: fixed;
    top: calc(var(--itc-bar-height) + 4px);
    right: 8px;
    bottom: auto;
    left: 8px;
    width: auto;
    max-height: calc(100dvh - var(--itc-bar-height) - 16px);
  }
}
</style>
